import { FC } from 'react';
import { Container } from '@chakra-ui/react';
import { isEmpty } from 'ramda';

import {
  DataRoomObjectFragment,
  useGetSearchResultsQuery,
} from '../../graphql/generated';
import { useSearchPageQueryParams } from '../../hooks/use-search-page-query-params/use-search-page-query-params';
import {
  DEFAULT_PAGE_SIZE,
  usePaginationConfig,
} from '../../hooks/use-search-pagination/use-search-pagination';
import { SearchResults } from '../search-results/search-results';
import {
  DateFilter,
  initialiseTreatyFilter,
  SearchToolbar,
} from '../search-results-toolbar/search-results-toolbar';
import { convertToISOString } from './date-utils/date-utils';

type Props = {
  treaties: DataRoomObjectFragment[];
};

export const SearchResultsContainer: FC<Props> = ({ treaties }) => {
  const {
    params,
    setPageParam,
    setTreatyParam,
    setSearchTermParam,
    setFromParam,
    setToParam,
  } = useSearchPageQueryParams();
  const paginationConfig = usePaginationConfig();

  const variables = {
    cedants: initialiseTreatyFilter(params.treaties, treaties),
    term: params.searchTerm,
    pagination: paginationConfig,
    from: convertToISOString(params.from, { startOfDay: true }),
    to: convertToISOString(params.to, { endOfDay: true }),
  };
  const getSearchResultsQuery = useGetSearchResultsQuery({
    variables,
    notifyOnNetworkStatusChange: true,
    skip: isEmpty(params.searchTerm),
  });

  const handleSearch = async (
    searchTerm: string,
    treaties: string[],
    from: DateFilter,
    to: DateFilter
  ) => {
    const fromDateStr = convertToISOString(from, { startOfDay: true });
    const toDateStr = convertToISOString(to, { endOfDay: true });

    await getSearchResultsQuery.refetch({
      cedants: treaties,
      term: searchTerm,
      pagination: { offset: 0, limit: DEFAULT_PAGE_SIZE },
      from: fromDateStr,
      to: toDateStr,
    });

    setSearchTermParam({ searchTerm });
    setPageParam({ page: '1' });
    setTreatyParam({ treaties });
    setFromParam(fromDateStr);
    setToParam(toDateStr);
  };

  return (
    <Container display="flex" flex="1" flexDirection="column" maxW="100%" p="8">
      <SearchToolbar
        treaties={treaties}
        onSearch={handleSearch}
        isLoading={getSearchResultsQuery.loading}
        count={getSearchResultsQuery.data?.search.count}
      />
      <SearchResults
        searchTerm={params.searchTerm}
        getSearchResults={getSearchResultsQuery}
      />
    </Container>
  );
};
